import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { useAppDispatch } from 'app/store';
import { subscriptionInfoSelector } from 'features/billing/subscription/subscriptionSlice';
import { SubscriptionPeriod, SubscriptionStatus } from 'features/billing/subscription/subscriptionTypes';
import { reactivateSubscription } from 'features/billing/subscription/subscriptionActions';
import { pricesSelector } from 'features/billing/prices/pricesSlice';
import { itemsTotalCountActiveMembersSelector } from 'features/members/membersSlice';
import { isLoadingUpcomingInvoiceSelector, upcomingInvoiceSelector } from 'features/billing/upcomingInvoice/upcomingInvoiceSlice';
import Button from 'components/shared/Button';
import TermsOfUse from 'components/shared/TermsOfUse';
import Modal from 'components/shared/Modal';
import styles from './SubscriptionDetails.module.scss';
import IntervalSelectionButtons from './IntervalSelectionButtons/IntervalSelectionButtons';
import CouponInfo from './CouponInfo/CouponInfo';
import PriceBreakdown from './PriceBreakdown/PriceBreakdown';
import SubscriptionQuantityUpdate from './SubscriptionQuantityUpdate/SubscriptionQuantityUpdate';
import { format } from 'date-fns';
import { billingFormsInfoSelector } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoSlice';
import { couponInfoSelector } from 'features/billing/couponInfo/couponInfoSlice';
import {
  setSubscriptionFormInfoInterval,
  setSubscriptionFormInfoQuantity
} from 'features/billing/formStates/billingFormsInfo/billingFormsInfoActions';
import { currenciesListSelector } from 'features/currencies/currenciesSlice';

interface SubscriptionDetailsProps {
  intervalSelectionEnabled: boolean;
  submitButtonHidden: boolean;
  handleSubmit: (quantity: number, priceId: string, couponId: string | null) => void;
}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = ({ intervalSelectionEnabled, submitButtonHidden, handleSubmit }) => {
  const dispatch = useAppDispatch();

  const currencies = useSelector(currenciesListSelector)
  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const couponInfo = useSelector(couponInfoSelector);
  const activeMembersCount = useSelector(itemsTotalCountActiveMembersSelector);
  const prices = useSelector(pricesSelector);
  const upcomingInvoice = useSelector(upcomingInvoiceSelector);
  const upcomingInvoiceIsLoading = useSelector(isLoadingUpcomingInvoiceSelector);

  const billingFormsInfo = useSelector(billingFormsInfoSelector);
  const { subscriptionFormInfo } = billingFormsInfo;

  const [termsOfUseAccepted, setTermsOfUseAccepted] = useState(false);
  const [priceBreakdownIsShown, setPriceBreakdownIsShown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subscriptionIsActive = subscriptionInfo!.status === SubscriptionStatus.Active;
  const subscriptionIsTrialing = subscriptionInfo!.status === SubscriptionStatus.Trialing;
  const isActiveOrTrialing = subscriptionIsActive || subscriptionIsTrialing;

  const findPrice = (interval: SubscriptionPeriod, productId: string, currency: string) => {
    return prices.find((price) =>
      price.interval === interval &&
      price.productId === productId &&
      price.currency === currency
    )
  }

  const handleQuantityChange = (quantity: number) => {
    dispatch(setSubscriptionFormInfoQuantity(quantity));
  }
  const handleIntervalChange = (interval: SubscriptionPeriod) => {
    dispatch(setSubscriptionFormInfoInterval(interval));
  }

  const handleSubmitUpdate = async () => {
    setTermsOfUseAccepted(false);
    setIsSubmitting(true);

    await handleSubmit?.(
      subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity,
      currentPrice!.id,
      subscriptionFormInfo?.couponId);

    setIsSubmitting(false);
  }

  const currentPrice = findPrice(
    subscriptionFormInfo?.interval ?? subscriptionInfo!.interval,
    subscriptionFormInfo?.productId ?? subscriptionInfo!.productId,
    subscriptionFormInfo?.currency ?? subscriptionInfo!.currency
  )
  const intervalOrQuantityChanged =
    subscriptionFormInfo?.quantity !== subscriptionInfo!.quantity ||
    subscriptionFormInfo?.interval !== subscriptionInfo!.interval

  const getCurrencySymbol = (currencyCode: string) => currencies.find(x => x.code.toLowerCase() === currencyCode.toLowerCase())?.symbol ?? currencyCode
  const currentCurrencySymbol = getCurrencySymbol(subscriptionFormInfo?.currency ?? subscriptionInfo!.currency)

  return (
    <div className={styles.subscriptionDetails}>
      {!subscriptionIsTrialing && <CouponInfo />}

      {subscriptionInfo?.cancelAtPeriodEnd &&
        <div className={`${styles.container} ${styles.bgLightRed}`}>
          <div className={`${styles.spaceBetween} ${styles.alignItemsCenter}`}>
            <p>Subscription will be canceled</p>
            <Button
              variant='default'
              onClick={() => dispatch(reactivateSubscription(subscriptionInfo.id))}
              style={{ maxWidth: 100 }}
            >
              Reactivate
            </Button>
          </div>
          <p className={`${styles.fontSizeS} ${styles.mt10} ${styles.fontGray}`}>
            Subscription will be canceled on {format(new Date(subscriptionInfo!.periodEnd), 'MMM dd, yyyy')}.
          </p>
        </div>
      }

      <div className={`${styles.container} ${styles.flexCol} ${styles.bgLightBlue}`}>
        {intervalSelectionEnabled &&
          <IntervalSelectionButtons
            interval={subscriptionFormInfo?.interval ?? subscriptionInfo!.interval}
            handleIntervalChange={handleIntervalChange}
          />
        }

        <div>
          <p>{!isActiveOrTrialing ? 'Selected plan:' : 'Current plan:'}</p>
          <p className={`${styles.currentPlan} ${styles.fontBlue} ${styles.fontSizeXL} ${styles.fontWeight600}`}>
            {currentPrice?.name}
          </p>
        </div>

        <div className={`${styles.flexCol} ${styles.spaceBetween}`}>
          <p className={`${styles.fontSizeL} ${styles.fontWeight600}`}>Total price</p>

          <div className={`${styles.spaceBetween} ${styles.alignItemsCenter}`}>
            <p className={`${styles.fontGray} ${styles.fontSizeS}`}>
              Subscription seats: {subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity} x {" "}
              {currentCurrencySymbol}{currentPrice?.amount}
            </p>

            <p className={`
              ${styles.fontSizeL} 
              ${(upcomingInvoice.discountAmount > 0 || couponInfo) ? styles.strikethrough : ''}
            `}>
              {currentCurrencySymbol}
              {((subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity) * (currentPrice?.amount ?? 0)).toFixed(2)}
            </p>
          </div>
        </div>

        {subscriptionInfo!.periodEnd &&
          <p className={`${styles.fontSizeS} ${styles.fontGray}`}>
            Next billing date: {format(new Date(subscriptionInfo!.periodEnd), 'MMM dd, yyyy')}
          </p>
        }

        {(!isActiveOrTrialing || intervalOrQuantityChanged) &&
          <div className={styles.flexCol}>
            <p className={`${styles.fontSizeL} ${styles.fontWeight600}`}>Total due today:</p>
            {upcomingInvoiceIsLoading ?
              <PulseLoader color="#0168fa" size={6} margin={4} speedMultiplier={1} />
              :
              subscriptionIsTrialing ?
                <p>
                  <span className={styles.strikethrough}>
                    {currentCurrencySymbol}{upcomingInvoice.amountDue}
                  </span>
                  <span>{" "} {currentCurrencySymbol}0 (free trial)</span>
                </p>
                :
                (upcomingInvoice.discountAmount > 0 || couponInfo) ?
                  <p>
                    {upcomingInvoice.discountAmount > 0 &&
                      <span className={styles.strikethrough}>
                        {currentCurrencySymbol}{upcomingInvoice.subtotal}
                      </span>
                    }

                    <span className={styles.fontWeightBold}>
                      {" "} {currentCurrencySymbol}{upcomingInvoice.amountDue}
                    </span>

                    {(upcomingInvoice.tax ?? 0) > 0 &&
                      <span>{" "}({currentCurrencySymbol}{upcomingInvoice.tax} Tax)</span>
                    }
                    <span>{" "}- discount coupon applied</span>
                  </p>
                  :
                  <p>
                    <span>{currentCurrencySymbol}{upcomingInvoice.amountDue}</span>

                    {((upcomingInvoice.tax ?? 0) > 0 && upcomingInvoice.amountDue > 0) &&
                      <span>{" "}({currentCurrencySymbol}{upcomingInvoice.tax} Tax)</span>
                    }
                  </p>
            }
          </div>
        }

        {
          (
            (subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity) > subscriptionInfo!.quantity &&
            subscriptionIsActive
          ) &&
          <Button
            variant='default'
            onClick={() => setPriceBreakdownIsShown(true)}
          >
            View price breakdown
          </Button>
        }
      </div>

      <SubscriptionQuantityUpdate
        quantity={subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity}
        activeMembersCount={activeMembersCount}
        handleQuantityChange={handleQuantityChange}
      />

      {(!submitButtonHidden && (!isActiveOrTrialing || intervalOrQuantityChanged)) &&
        <div>
          {!subscriptionIsTrialing &&
            <TermsOfUse
              termsAccepted={termsOfUseAccepted}
              setTermsAccepted={setTermsOfUseAccepted}
            />
          }

          <div className={styles.updateButton}>
            <Button
              variant="primary"
              onClick={handleSubmitUpdate}
              disabled={(!termsOfUseAccepted && !subscriptionIsTrialing) || isSubmitting}
              preloader={isSubmitting}
            >
              {subscriptionIsActive ? 'Update and pay' : subscriptionIsTrialing ? 'Update' : 'Confirm and pay'}
            </Button>
          </div>
        </div>
      }

      <Modal
        title={"Price breakdown"}
        open={priceBreakdownIsShown}
        onClose={() => setPriceBreakdownIsShown(false)}
      >
        <PriceBreakdown newQuantity={subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity} />
      </Modal>
    </div>
  );
};

export default SubscriptionDetails;
